import type { PixelCrop } from "react-image-crop";

// maximum recommended dimensions for the logo
export const MAX_WIDTH = 1000;
export const MAX_HEIGHT = 1000;

// minimum recommended dimensions for quality
export const MIN_WIDTH = 100;
export const MIN_HEIGHT = 100;

// this is to fix a memory leak when canvas operations happen on unmounted components
export function canvasPreview(
  image: HTMLImageElement,
  canvas: HTMLCanvasElement,
  crop: PixelCrop,
  preserveTransparency = true,
) {
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return;
  }

  // determine canvas size based on crop dimensions
  // but keep within reasonable bounds
  const aspectRatio = crop.width / crop.height;
  let canvasWidth: number, canvasHeight: number;

  if (aspectRatio >= 1) {
    // landscape or square
    canvasWidth = Math.min(crop.width, MAX_WIDTH);
    canvasHeight = canvasWidth / aspectRatio;
  } else {
    // portrait
    canvasHeight = Math.min(crop.height, MAX_HEIGHT);
    canvasWidth = canvasHeight * aspectRatio;
  }

  canvas.width = canvasWidth;
  canvas.height = canvasHeight;

  ctx.clearRect(0, 0, canvas.width, canvas.height);

  // if not preserving transparency (e.g., for JPG files), add a white background
  if (!preserveTransparency) {
    ctx.fillStyle = "white";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }

  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;

  const pixelCrop = {
    x: crop.x * scaleX,
    y: crop.y * scaleY,
    width: crop.width * scaleX,
    height: crop.height * scaleY,
  };

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    canvas.width,
    canvas.height,
  );
}

// function to create a blob from a canvas
export function canvasToBlob(
  canvas: HTMLCanvasElement,
  format: string,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Canvas is empty"));
          return;
        }
        resolve(blob);
      },
      format,
      1,
    );
  });
}

// function to check transparency in an image
export function checkImageTransparency(
  imageElement: HTMLImageElement,
): boolean {
  const { naturalWidth, naturalHeight } = imageElement;

  // create a temporary canvas to check for transparency
  const tempCanvas = document.createElement("canvas");
  tempCanvas.width = naturalWidth;
  tempCanvas.height = naturalHeight;
  const ctx = tempCanvas.getContext("2d");

  if (!ctx) {
    return false;
  }

  ctx.drawImage(imageElement, 0, 0);

  // check if any pixel has alpha < 255 (fully opaque)
  const imageData = ctx.getImageData(0, 0, naturalWidth, naturalHeight);
  const data = imageData.data;

  for (let i = 3; i < data.length; i += 4) {
    if (data[i] < 255) {
      return true;
    }
  }

  return false;
}
